// frontend/src/components/Sidebar.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Link, NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import ThemeToggle from "./ThemeToggle";
import { useSocket } from "../contexts/SocketContext";
import { useAuth } from "../contexts/AuthContext";
import "./Sidebar.css";

// A global reference so SurveyDetails can trigger a re-fetch
let globalSidebarRef = null;

/** Return the function we stored in globalSidebarRef (if any) */
export function getSidebarNotificationsRef() {
  return globalSidebarRef;
}

const Sidebar = ({ userName, onLogout, onPurchaseTokens, isDarkMode }) => {
  const socket = useSocket();
  const { user } = useAuth();

  // Local states
  const [notifications, setNotifications] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0); // Total unread notifications

  // 1) Fetch Surveys
  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/surveys");
      setSurveys(data.surveys || []);
      console.log("Fetched surveys:", data.surveys || []);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load surveys."
      );
    } finally {
      setLoading(false);
    }
  };

  // 2) Fetch Notifications
  const fetchNotifications = async () => {
    try {
      const { data } = await axiosInstance.get("/notifications");
      setNotifications(data);

      const unreadCommentCount = data.filter(
        (notif) => notif.type === "comment" && !notif.isRead
      ).length;
      setUnreadCount(unreadCommentCount);

      console.log("Fetched notifications:", data);
      console.log("Total unread notifications:", unreadCommentCount);
    } catch (err) {
      toast.error(`Failed to fetch notifications: ${err.message}`);
    }
  };

  // On mount, fetch both surveys & notifications, store ref
  useEffect(() => {
    fetchSurveys();
    fetchNotifications();

    // Expose fetchNotifications globally so SurveyDetails can call it
    globalSidebarRef = fetchNotifications;
    return () => {
      globalSidebarRef = null; // clear on unmount
    };
  }, []);

  // 3) Socket listener for new comments
  useEffect(() => {
    if (!socket) return;

    const handleNewComment = (data) => {
      console.log("New comment event in Sidebar:", data);
      setNotifications((prev) => {
        const updated = [
          ...prev,
          {
            _id: data.notificationId || Date.now(),
            userId: user?._id,
            surveyId: data.surveyId,
            questionId: data.questionId,
            message: `New comment: "${data.commentText}"`,
            isRead: false,
            createdAt: new Date(data.createdAt || Date.now()).toISOString(),
            type: "comment",
          },
        ];

        // Increment total unread by one
        setUnreadCount((count) => count + 1);

        return updated;
      });
    };

    socket.on("newComment", handleNewComment);

    return () => {
      socket.off("newComment", handleNewComment);
    };
  }, [socket, user?._id]);

  // 4) Compute which surveys have unread comments
  const surveysWithUnread = useMemo(() => {
    return surveys.map((survey) => {
      // If you do not populate surveyId, it should be a plain string
      const hasUnread = notifications.some(
        (notif) =>
          notif.type === "comment" &&
          !notif.isRead &&
          String(notif.surveyId?._id || notif.surveyId) === String(survey._id)
      );
      return { ...survey, hasUnread };
    });
  }, [surveys, notifications]);

  // 5) Render states
  if (loading) {
    return (
      <div className="sidebar">
        <div className="loading-state">Loading surveys...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sidebar">
        <p className="error-message">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="sidebar">
      <Link to="/dashboard" className="logo-link">
        <img src="/survailogo.svg" alt="Surv.ai Logo" className="logo" />
      </Link>
      <div className="survey-section">
        <div className="subtitle-container">
          <h2 className="subtitle">Latest surveys</h2>
        </div>
        {surveysWithUnread.length === 0 ? (
          <p>No surveys found. Please create a new one!</p>
        ) : (
          <div className="survey-list-container">
            <div className="survey-list">
              {surveysWithUnread.slice(0, 10).map((survey) => (
                <div className="survey-list__item" key={survey._id}>
                <Link to={`/surveys/${survey._id}/details`} className="survey-link-grid">
                  {/* Column 1: text that can wrap */}
                  <div className="survey-text">
                    {survey.title?.length > 50
                      ? survey.title.substring(0, 50) + "..."
                      : survey.title || "Untitled Survey"}
                  </div>
              
                  {/* Column 2: a block containing the bell */}
                  {survey.hasUnread && (
                    <div className="bell-block">
                      <i className="fa-solid fa-bell bell-icon" />
                    </div>
                  )}
                </Link>
              </div>
              ))}
            </div>
          </div>
        )}
        {surveysWithUnread.length > 10 && (
          <NavLink to="/surveys" className="view-all-button full-width">
            View all surveys
          </NavLink>
        )}
      </div>

      <div className="user-info">
        <span className="welcome">Welcome, {userName || "User"}</span>
        <button onClick={onPurchaseTokens} className="purchase-btn">
          Purchase Tokens
        </button>
        <div className="icon-buttons">

           {/* Conditionally show Org Admin link if role is orgAdmin */}
        {user?.role === "orgAdmin" && (
          <li>
            <Link to="/org-admin">Organization Dashboard</Link>
          </li>
        )}
        
          {/* Only show if user has godAdmin role */}
  {user?.role === "godAdmin" && (
    <NavLink
      to="/god-admin"
      className="god-admin-icon"
      aria-label="God Admin"
    >
      <i class="fa-regular fa-user-beard-bolt"></i>
    </NavLink>
  )}
          <button className="settings-icon" aria-label="Settings">
            <i className="fa-solid fa-gear"></i>
          </button>
          <ThemeToggle isDarkMode={isDarkMode} />
          <button onClick={onLogout} className="logout-button" aria-label="Logout">
            <i className="fa-regular fa-arrow-right-from-bracket"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
